import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FeedbackService } from '../../_services/api/feedback.service';
import { NotificationService } from '../../_services/app/notification.service';
import { FeedbackResponse, FeedbackStatusLabels, FeedbackStatusColors } from '../../_models/feedback.model';
import { ApiResponse } from '../../_models/api-response.model';

@Component({
  selector: 'app-feedback-details',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './feedback-details.component.html',
  styleUrls: ['./feedback-details.component.scss']
})
export class FeedbackDetailsComponent implements OnInit, OnDestroy {
  feedbackData!: FeedbackResponse;
  reservationId?: number;
  isLoaded = false;
  isLoading = false;
  errorMessage = '';
  
  frenchTranslations = {
    title: 'Mes Évaluations',
    subtitle: 'Historique de vos évaluations',
    noFeedbacks: 'Aucune évaluation pour le moment',
    overallRating: 'Note globale',
    serviceQuality: 'Qualité',
    punctuality: 'Ponctualité',
    communication: 'Communication',
    reservation: 'Réservation',
    eventName: 'Événement',
    submittedOn: 'Soumis le',
    updateCount: 'Mise à jour',
    times: 'fois',
    viewDetails: 'Voir détails',
    editFeedback: 'Modifier',
    statusLabels: FeedbackStatusLabels
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    private feedbackService: FeedbackService,
    private notification: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
   this.loadFromRoute();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

   private loadFromRoute(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
       const id = params.get('reservationId');
        if (id) {
          this.reservationId = Number(id);
          this.loadFeedback();
        }
    });
  }

  private loadFeedback(): void {
    if (!this.reservationId) return;
    
    this.isLoading = true;
    this.errorMessage = '';
    
    this.feedbackService.getFeedback(this.reservationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<FeedbackResponse>) => {
          if (response.success && response.data) {
            this.feedbackData = response.data;
          } else {
            this.isLoaded = true;
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading feedback:', error);
           if (error.status === 409 || error.status === 403) {
            this.router.navigate(['/member/reservations/details', this.reservationId]);
            return;
          }
          this.errorMessage = error.error?.message || 'Erreur de chargement';
          this.isLoading = false;
        }
      });
  }

  getStatusColor(status: string): string {
    return FeedbackStatusColors[status as keyof typeof FeedbackStatusColors] || '#6c757d';
  }

  getStatusText(status: string): string {
    return this.frenchTranslations.statusLabels[status as keyof typeof FeedbackStatusLabels] || status;
  }

  canEditFeedback(feedback: FeedbackResponse): boolean {
    return feedback.updateCount < 2;
  }

  editFeedback(feedback: FeedbackResponse): void {
    this.router.navigate(['/member/feedback', feedback.reservationId]);
  }

  viewReservation(reservationId: number): void {
    this.router.navigate(['/member/reservations/details', reservationId]);
  }

  getAverageRating(feedback: FeedbackResponse): number {
    const ratings = [
      feedback.overallRating,
      feedback.serviceQualityRating,
      feedback.punctualityRating,
      feedback.communicationRating
    ];
    return ratings.reduce((a, b) => a + b, 0) / ratings.length;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  getNote(feedback: any){
    return Math.round(this.getAverageRating(feedback));
  }
}